/**
 * Хук загрузки ленты активности с cursor-пагинацией
 * Два источника: элементы вишлистов друзей + обновления избранных вишлистов
 * Дедупликация: если владелец в друзьях И в избранном → приоритет друзьям
 */

import type { UseInfiniteQueryResult, InfiniteData } from '@tanstack/react-query';
import { useAuthInfiniteQuery } from '@/hooks';
import { feedQueryKeys } from '@/lib';
import { serverGet } from '@/lib';
import type { FeedPage } from '../model';

/** Количество постов на одну страницу */
const FEED_PAGE_SIZE = 20;

interface UseFeedQueryParams {
  /** ID текущего пользователя (запрос не выполняется если пустой) */
  userId: string | undefined;
}

/**
 * Хук загрузки ленты активности с бесконечным скроллом
 */
export function useFeedQuery({
  userId,
}: UseFeedQueryParams): UseInfiniteQueryResult<InfiniteData<FeedPage>, Error> {
  return useAuthInfiniteQuery<FeedPage, Error, InfiniteData<FeedPage>, readonly ['feed', string | undefined], string | undefined>(
    userId,
    {
      queryKey: feedQueryKeys.feed(userId),

      queryFn: async ({ pageParam }): Promise<FeedPage> => {
        const params = new URLSearchParams({ limit: String(FEED_PAGE_SIZE) });

        if (pageParam) {
          params.set('cursor', pageParam);
        }

        return serverGet<FeedPage>(`/feed?${params}`);
      },

      initialPageParam: undefined,

      getNextPageParam: (lastPage): string | undefined =>
        lastPage.hasMore && lastPage.nextCursor
          ? lastPage.nextCursor
          : undefined,

      staleTime: 2 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    },
  );
}
